<template>
  <DashboardPanelTemplate
    :panel-size="panelSize"
    :panel-focus="panelFocus"
    :loading="loading"
    :error="loadingError"
    :error-message="$t('ErrorMessage')"
  >
    <div class="carousel">
      <div class="carousel-content">
        <div>
          <div class="icon-chevron-left font-size-26" @click="previousClick" />
        </div>
        <transition-group v-if="!loading && slides[0]" class="slide-viewport" tag="div">
          <div
            v-for="(slide, index) in shuffledSlides"
            :key="slide.ContentId"
            class="slide"
            :class="{'opacity-5': index !== centerIndex}"
          >
            <div class="slide-title">
              <h4>{{ slide.Title }}</h4>
            </div>
            <div class="slide-description">
              <p>{{ slide.Description }}</p>
            </div>
            <div class="slide-button-wrapper">
              <div class="col">
                <sb-button
                  :id="buttonId(slide.ContentId)"
                  variation="primary"
                  :color="'blue'"
                  @click="buttonClick(slide.ContentId)"
                >
                  {{ slide.ButtonText }}
                </sb-button>
              </div>
            </div>
          </div>
          <div v-if="useExtraCenteringSlide" key="-1" class="slide opacity-5" />
        </transition-group>
        <div>
          <div class="icon-chevron-left rotate-180 font-size-26" @click="nextClick" />
        </div>
      </div>
      <div class="carousel-footer">
        <div
          v-for="index in slideCount"
          :key="index"
          class="location-marker rounded-circle"
          :class="{
            'bg-primary': index === slideShowing,
            'bg-gray-400': index !== slideShowing
          }"
        />
      </div>
    </div>
  </DashboardPanelTemplate>
</template>

<script>
import DashboardPanelTemplate from '@/components/layouts/DashboardPanelTemplate'
import { getTelemetryInstance } from '@/telemetry'

export default {
  name: 'DashboardCarouselPanel',
  components: {
    DashboardPanelTemplate
  },
  props: {
    panelSize: {
      type: String,
      required: true
    },
    panelFocus: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      datastoreLocation: 'dashboard/carousel/',
      announcementsLocation: 'dashboard/announcements/',
      carouselAutocycleTimerMS: 8000,
      slideShowing: 1,
      autoCycle: undefined,
      slidesIndexArray: []
    }
  },
  computed: {
    loadingError () {
      return this.$shipBobStore.getters[this.datastoreLocation + 'loadingError']
    },
    slides () {
      const announcements = this.$shipBobStore.getters[this.announcementsLocation + 'announcements']
      const slides = this.$shipBobStore.getters[this.datastoreLocation + 'slides']

      const mappedAnnouncements = (announcements || [])
        .map(a => ({
          ButtonText: 'Learn more',
          Description: null,
          RedirectURL: a.Link,
          Title: a.Description
        }))

      const combined = [
        ...mappedAnnouncements,
        ...(slides || [])
      ].map((c, i) => {
        c.ContentId = i

        return c
      })

      return combined
    },
    slideCount () {
      return (this.slides) ? this.slides.length : 0
    },
    shuffledSlides () {
      if (this.slidesIndexArray.length === 0) return []
      return new Array(this.slideCount).fill().map((x, i) => this.slides[this.slidesIndexArray[i]])
    },
    centerIndex () {
      return Math.floor(this.slideCount / 2)
    },
    useExtraCenteringSlide () {
      return (this.slideCount % 2 === 0)
    }
  },
  watch: {
    slides () {
      this.startSlides()
    }
  },
  created () {
    this.$shipBobStore.dispatch(this.announcementsLocation + 'getAnnouncements')
    this.$shipBobStore.dispatch(this.datastoreLocation + 'getSlides')
  },
  beforeDestroy () {
    window.clearInterval(this.autoCycle)
  },
  destroyed () {
    this.$shipBobStore.unregisterModule(['dashboard', 'carousel'])
  },
  methods: {
    startSlides () {
      this.slidesIndexArray = new Array(this.slideCount).fill().map((x, i) => i)
      this.shuffleArray(this.slidesIndexArray)
      this.autoCycle = window.setInterval(() => this.next(), this.carouselAutocycleTimerMS)
    },
    nextClick () {
      this.stopAutocycle()
      this.next()
    },
    buttonClick (contentId) {
      const url = this.slides.find(x => x.ContentId === contentId).RedirectURL
      getTelemetryInstance()?.trackEvent({
        name: 'MERCHANT_APP_DASHBOARD_CAROUSAL_CLICKED',
        properties: {
          contentId,
          url
        }
      })
      if (url) window.open(url, '_blank')
    },
    previousClick () {
      this.stopAutocycle()
      this.previous()
    },
    next () {
      this.slideShowing++
      if (this.slideShowing > this.slideCount) { this.slideShowing = 1 }
      const first = this.slidesIndexArray.shift()
      this.slidesIndexArray = this.slidesIndexArray.concat(first)
    },
    previous () {
      this.slideShowing--
      if (this.slideShowing <= 0) { this.slideShowing = this.slideCount }
      const last = this.slidesIndexArray.pop()
      this.slidesIndexArray = [last].concat(this.slidesIndexArray)
    },
    buttonId (contentId) {
      return 'dashboard-carousel-button-' + contentId
    },
    goToSlide (slideNumber) {
      this.slideShowing = slideNumber
    },
    stopAutocycle () {
      clearInterval(this.autoCycle)
    },
    shuffleArray (array) {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]]
      }
    }
  },
  i18n: {
    messages: {
      'en-US': {
        ErrorMessage: 'Hm... we couldn\'t load your content. Please refresh your browser and if you have issues, let our support team know.'
      }
    }
  }
}
</script>
<style scoped lang='scss'>
@import '@/../assets/scss/shipbob-custom/components/dashboard/_panel-dashboard-carousel.scss';
</style>
